import type { CalendarFeed, FeedItem } from "@ronin/contracts";
import { Button, calendarColors, cn, Dialog } from "@ronin/ui";
import { Clock, MapPin } from "lucide-react";
import { describeTiming } from "./item-style.js";

interface ItemDialogProps {
  item: FeedItem | null;
  feeds: ReadonlyMap<string, CalendarFeed>;
  timeZone: string;
  onClose: () => void;
}

/** Read-only details of a feed event (spec03 FR11): no edit, no delete. */
export function ItemDialog({ item, feeds, timeZone, onClose }: ItemDialogProps) {
  const feed = item ? feeds.get(item.feedId) : undefined;
  return (
    <Dialog
      open={item !== null}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={item?.title ?? ""}
      description={feed ? `From ${feed.name}` : undefined}
    >
      {item && (
        <div className="flex flex-col gap-3 text-sm">
          <p className="flex items-center gap-2 text-secondary-foreground">
            <Clock className="size-4 shrink-0 text-dim-foreground" aria-hidden />
            {describeTiming(item, timeZone)}
          </p>
          {item.location && (
            <p className="flex items-center gap-2 text-secondary-foreground">
              <MapPin className="size-4 shrink-0 text-dim-foreground" aria-hidden />
              <span className="break-words">{item.location}</span>
            </p>
          )}
          {item.description && (
            <p className="max-h-64 overflow-y-auto border-t pt-3 text-xs whitespace-pre-wrap break-words text-muted-foreground">
              {item.description}
            </p>
          )}
          {feed && (
            <p className="flex items-center gap-2 text-xs text-dim-foreground">
              <span
                className={cn("size-3 shrink-0", calendarColors[feed.color].swatch)}
                aria-hidden
              />
              {feed.name} · read-only
            </p>
          )}
          <div className="mt-2 flex justify-end">
            <Button type="button" variant="ghost" onClick={onClose} data-autofocus>
              Close
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
